import { Navigate, Route, Routes } from "react-router-dom";

import { DelgenticLanding } from "../src/features/landing/DelgenticLanding";
import { CookiePolicyPage } from "../src/features/legal/CookiePolicyPage";
import { PrivacyPage } from "../src/features/legal/PrivacyPage";
import { TermsPage } from "../src/features/legal/TermsPage";
// Scheda's home. a2a.delgentic.com still works (it remains an alias on the same distribution),
// but new visitors should be sent to the name the product actually has.
// Single source shared with the landing components (the nav's products menu and
// the agent-card CTA link into the app too).
import { schedaAppUrl } from "../src/features/landing/siteUrls";

/**
 * Auth lives in the app, not here. Carrying the mode across as ?auth= means the app opens the
 * right pane of its sign-in modal straight away, so the hand-off is still one click.
 *
 * <p>The parameter is optional so this is assignable to the zero-argument callbacks the
 * marketing and legal components take ({@code onGoToApp}, {@code onSignup}).
 */
function goToApp(mode?: "signin" | "signup"): void {
  window.location.href = schedaAppUrl(mode);
}

/**
 * The advert's CTA says "Try Scheda", so it lands on Scheda's own landing page and lets the
 * visitor decide there. It deliberately does NOT open sign-up: someone arriving from the
 * company page has not seen the product yet, and a modal is a poor first impression of it.
 */
const goToScheda = (): void => goToApp();

/** The footer's CTA is "Get started free", which is a request for an account. */
const goToSignup = (): void => goToApp("signup");

/**
 * The apex route tree, split out of {@code main.tsx} so it can be rendered by a test without
 * the entry point's module side effects (i18n touches `document` at import time).
 *
 * <p>THE INVARIANT THIS FILE CARRIES: nothing reachable from here may call {@code useSession()}
 * or {@code useAuth()}. There is no {@code SessionProvider} and no OIDC provider in this bundle,
 * so such a call throws during the first render and CloudFront serves delgentic.com an empty
 * page. That has happened twice — {@code LegalPageLayout}/{@code useAuth} (2026-07-13) and
 * {@code AuthModal}/{@code useSession} (2026-08-01). {@code CatalogApp.test.tsx} is the guard.
 *
 * <p>The apex renders {@link DelgenticLanding}, NOT the app's {@code LandingPage}. They were the
 * same component until #170 rewrote it into Scheda's product landing, at which point the apex
 * started serving the wrong brand under a Delgentic title. Keep them separate.
 */
export function CatalogApp() {
  return (
    <Routes>
      <Route path="/" element={<DelgenticLanding onTryScheda={goToScheda} onSignup={goToSignup} />} />
      {/* brand="delgentic": the apex is the platform, not the product. The app
          bundle serves the SAME two pages under Scheda's brand. Body copy is
          identical either way, and Delgentic ApS is the controller on both. */}
      <Route
        path="/privacy"
        element={<PrivacyPage brand="delgentic" onSignup={goToSignup} />}
      />
      <Route
        path="/cookies"
        element={<CookiePolicyPage brand="delgentic" onSignup={goToSignup} />}
      />
      {/* /terms is the URL given to Google for verification, so the path is fixed:
          repointing it later would put the consent screen back through review. */}
      <Route
        path="/terms"
        element={<TermsPage brand="delgentic" onSignup={goToSignup} />}
      />
      {/* The apex serves only the public surface; anything else goes home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
